import React, { useEffect, useState } from 'react';
import './App.css';
import { Routes, Route } from 'react-router-dom';
import VendorDashboard from 'pages/dashboard/VendorDashboard';
import Products from 'pages/products/Products';
import ProductStats from 'pages/products/ProductStats';
import ProductDetails from 'pages/products/ProductDetails';
import CloneProduct from 'pages/products/CloneProduct';
import VendorDashboardNavigation from 'pages/dashboard/VendorDashboardNavigation';
// import AddNewProduct from 'pages/addProduct/AddNewProduct';
import Orders from 'pages/orders/Orders';
import OrderDetails from 'pages/orders/OrderDetails';
import OrderEdit from 'pages/orders/OrderEdit';
import Stats from 'pages/stats/Stats';
import Materials from 'pages/materials/Materials';
import Reports from 'pages/reports/Reports';
import axios from 'axios';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  Toolbar,
  Tooltip,
  Typography
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useGoogleLogin } from '@react-oauth/google';
import { clearAdminSession, getAdminSession, storeAdminSession } from './auth';

axios.defaults.baseURL =
  process.env.NODE_ENV === 'development' ? 'http://localhost:6101/admin/api' : '/admin/api';

//TODO: extract paths in constants. Same paths are also in VendorDashboardNavigation so replace both.
function App() {
  const [isAuth, setIsAuth] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [toast, setToast] = useState(null);

  const handleLoginSuccess = async (tokenResponse) => {
    setIsLoggingIn(true);
    setToast(null);

    try {
      const response = await axios.post('/auth/google', {
        accessToken: tokenResponse.access_token
      });

      storeAdminSession(response.data);
      setUser(response.data.user);
      setIsAuth(true);
    } catch (error) {
      clearAdminSession();
      setIsAuth(false);
      setToast(
        error.response?.status === 403
          ? 'Този Google профил няма достъп до администрацията.'
          : error.response?.data?.message || 'Влизането с Google беше неуспешно.'
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogin = useGoogleLogin({
    onSuccess: handleLoginSuccess,
    onError: () => setToast('Влизането с Google беше отказано.'),
    scope: 'openid email profile'
  });

  const handleLogout = async () => {
    try {
      await axios.post('/auth/logout');
    } catch (error) {
      // Clear the local session even if the server is temporarily unavailable.
    } finally {
      clearAdminSession();
      setUser(null);
      setIsAuth(false);
    }
  };

  useEffect(() => {
    const checkStoredSession = async () => {
      if (!getAdminSession()) {
        setIsAuth(false);
        return;
      }

      try {
        const response = await axios.get('/auth/session');
        setUser(response.data.user);
        setIsAuth(true);
      } catch (error) {
        clearAdminSession();
        setIsAuth(false);
      }
    };

    const handleExpiredSession = () => {
      setUser(null);
      setIsAuth(false);
      setToast('Сесията ви изтече. Моля, влезте отново.');
    };

    checkStoredSession();
    window.addEventListener('admin-auth-expired', handleExpiredSession);
    return () => window.removeEventListener('admin-auth-expired', handleExpiredSession);
  }, []);

  if (isAuth === null) {
    return (
      <Box className="auth-page">
        <CircularProgress aria-label="Проверка на сесията" />
      </Box>
    );
  }

  return (
    <>
      {!isAuth && (
        <Box className="auth-page">
          <Paper className="auth-card" elevation={5}>
            <Box
              component="img"
              src="/gotobed_logo.jpg"
              alt="GoToBed"
              className="brand-logo brand-logo--large"
            />
            <Typography variant="h4" component="h1">
              GoToBed Admin
            </Typography>
            <Typography color="text.secondary">
              Влезте с одобрен Google профил, за да продължите.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<GoogleIcon />}
              disabled={isLoggingIn}
              onClick={() => handleLogin()}
            >
              {isLoggingIn ? 'Влизане...' : 'Вход с Google'}
            </Button>
            {toast && (
              <Typography color="error" role="alert">
                {toast}
              </Typography>
            )}
          </Paper>
        </Box>
      )}
      {isAuth && (
        <>
          <AppBar
            position="sticky"
            color="inherit"
            elevation={0}
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'rgba(255,255,255,.94)'
            }}
          >
            <Container maxWidth="xl">
              <Toolbar disableGutters sx={{ minHeight: { xs: 64, sm: 72 }, gap: { xs: 1, sm: 3 } }}>
                <Stack direction="row" alignItems="center" gap={1.25} sx={{ flexShrink: 0 }}>
                  <Box
                    component="img"
                    src="/gotobed_logo.jpg"
                    alt="GoToBed"
                    className="brand-logo"
                  />
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>GoToBed</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Administration
                    </Typography>
                  </Box>
                </Stack>

                <VendorDashboardNavigation />
                <Box sx={{ flexGrow: 1 }} />

                <Stack direction="row" alignItems="center" gap={1}>
                  <Avatar
                    src={user?.picture || undefined}
                    alt={user?.name || user?.email}
                    sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 14 }}
                  >
                    {(user?.name || user?.email || 'A').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ display: { xs: 'none', lg: 'block' }, maxWidth: 210 }}>
                    <Typography variant="body2" fontWeight={650} noWrap>
                      {user?.name || 'Администратор'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {user?.email}
                    </Typography>
                  </Box>
                  <Tooltip title="Изход">
                    <IconButton onClick={handleLogout} aria-label="Изход">
                      <LogoutRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Toolbar>
            </Container>
          </AppBar>
          <Box component="main">
            <Routes>
              <Route path="/" element={<VendorDashboard />} />
              <Route path="/dashboard" element={<VendorDashboard />} />
              <Route path="/products" element={<Products />} />
              <Route path="/product-stats" element={<ProductStats />} />
              <Route path="/productDetails/:id" element={<ProductDetails />} />
              <Route path="/cloneProduct/:id" element={<CloneProduct />} />
              {/* <Route path="/addNewProduct" element={<AddNewProduct />} /> */}
              <Route path="/orders" element={<Orders />} />
              <Route path="/materials" element={<Materials />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/orderDetails/:id" element={<OrderDetails />} />
              <Route path="/orderDetails/:id/edit" element={<OrderEdit />} />
              <Route path="/stats" element={<Stats />} />
            </Routes>
          </Box>
        </>
      )}
    </>
  );
}

export default App;
